import { TalentPoolActions } from 'components/custom/company/talent-pool-actions';
import { TalentActions } from 'components/custom/talent/actions-menu';
import { ActionItem } from 'components/custom/talent/types';
import { useIsTalentInCurrentCompanyPool } from 'hooks/company';
import { useTalentName } from 'hooks/talents';
import React from 'react';
import { Link } from 'react-router-dom';
import { pathManager } from 'routes';

import { Box, Grid, Paper, useMediaQuery, useTheme } from '@mui/material';
import { makeStyles } from '@mui/styles';

import { Talent, TalentSearchResult } from '@libs/graphql-types';
import { stopEvent } from '@libs/helpers/common';

import CategoriesList from './CategoriesList';
import Highlights from './Highlights';
import { JobApplicationChip } from './JobApplicationChip';
import LogoBlock from './LogoBlock';
import MainInfo from './MainInfo';
import TitleSection from './TitleSection';

interface TalentCardV2Props {
  talentSearch: TalentSearchResult;
  refetch: VoidFunction;
  showJobApplicationBadge?: boolean;
  filterActions?: (actions: ActionItem[]) => ActionItem[];
  scopedCompanyId?: string;
}

const useStyles = makeStyles((theme) => ({
  root: {
    transition: 'background .3s',
    padding: theme.spacing(6),
    display: 'flex',
    flexDirection: 'column',

    [theme.breakpoints.down('md')]: {
      padding: theme.spacing(4),
    },
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(3),
    },

    '&:hover': {
      background: 'rgba(25, 25, 25, 0.08)',
    },
  },
  mainGrid: {
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      '& > .MuiGrid-item:first-child': {
        marginBottom: theme.spacing(3),
        alignSelf: 'center',
      },
    },
  },
  contentGrid: {
    [theme.breakpoints.down('sm')]: {
      textAlign: 'center',
    },
  },
  mobileActions: {
    marginTop: theme.spacing(3),
    '& button': {
      width: '100%',
    },
  },
}));

const TalentCardV2 = (props: TalentCardV2Props) => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const {
    showJobApplicationBadge,
    talentSearch,
    refetch,
    filterActions,
    scopedCompanyId,
  } = props;
  const talent = talentSearch?.talent as Talent;
  const isInPool = useIsTalentInCurrentCompanyPool({ talent, scopedCompanyId });

  const highlights = talentSearch?.highlights || [];
  const name = useTalentName(talent);
  const talentLink = pathManager.company.talentProfile.generatePath({
    id: talent.id,
  });

  const jobApplicationBadge = showJobApplicationBadge ? (
    <JobApplicationChip talent={talent} />
  ) : undefined;

  return (
    <div>
      <Paper
        className={classes.root}
        component={Link}
        data-test-id="talent-card"
        elevation={0}
        to={talentLink}
      >
        <Grid wrap="nowrap" spacing={3} container className={classes.mainGrid}>
          <Grid item>
            <LogoBlock name={name} talent={talent} />
          </Grid>
          <Grid flexGrow={1} item className={classes.contentGrid}>
            <TitleSection
              applicationBage={jobApplicationBadge}
              refetch={refetch}
              talent={talent}
              isInPool={isInPool}
              name={name}
              filterActions={filterActions}
              hideActions={isMobile}
            />

            <MainInfo talent={talent} />

            <CategoriesList talent={talent} />
          </Grid>
        </Grid>

        {!!highlights.length && (
          <Box mt={3}>
            <Highlights items={highlights} />
          </Box>
        )}

        {isMobile && (
          <Box className={classes.mobileActions} onClick={stopEvent}>
            {!!isInPool ? (
              <TalentActions
                talent={talent}
                refetch={refetch}
                filterActions={filterActions}
              />
            ) : (
              <TalentPoolActions talent={talent} refetch={refetch} />
            )}
          </Box>
        )}
      </Paper>
    </div>
  );
};

export default TalentCardV2;
